import React, { useContext, useEffect, useReducer, useState } from "react";

import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";

import { makeStyles } from "@material-ui/core/styles";

import {
  Android,
  DeleteOutline,
  Edit,
  PowerSettingsNew,
  Search,
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import PromptModal from "../../components/PromptModal";
import { toast } from "../../utils/toast";
import ConfirmationModal from "../../components/ConfirmationModal";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { SocketContext } from "../../context/Socket/SocketContext";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  agentInfoBox: {
    background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 16,
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(2),
  },
  agentInfoText: {
    lineHeight: 1.7,
    color: theme.palette.text.primary,
  },
  agentLinks: {
    marginTop: theme.spacing(1),
    lineHeight: 1.9,
  },

  // Status chips — pastel outlined style
  chipActive: {
    border: `1.5px solid #43a047`,
    color: "#43a047",
    fontWeight: 700,
    fontSize: "0.70rem",
    backgroundColor: "rgba(67,160,71,0.08)",
    borderRadius: 8,
  },
  chipInactive: {
    border: `1.5px solid ${theme.palette.grey[400]}`,
    color: theme.palette.grey[500],
    fontWeight: 700,
    fontSize: "0.70rem",
    backgroundColor: "rgba(0,0,0,0.04)",
    borderRadius: 8,
  },

  // Queue chips
  queueChip: {
    margin: 2,
    height: 22,
    fontSize: "0.7rem",
    borderRadius: 6,
  },
  allQueuesChip: {
    margin: 2,
    height: 22,
    fontSize: "0.7rem",
    borderRadius: 6,
    borderColor: theme.palette.text.disabled,
    color: theme.palette.text.disabled,
  },

  // Agent name with icon
  agentCell: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
  },
  agentIcon: {
    fontSize: 17,
    color: theme.palette.primary.main,
    opacity: 0.75,
  },

  // Empty state
  emptyStateRow: {
    "& td": {
      border: "none",
    },
  },
  emptyStateBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(6, 2),
    gap: theme.spacing(1.5),
  },
  emptyStateIcon: {
    fontSize: 64,
    color: theme.palette.text.disabled,
    opacity: 0.35,
  },
  emptyStateText: {
    color: theme.palette.text.disabled,
    fontSize: "0.95rem",
    fontWeight: 500,
  },

  // Search field
  searchField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      height: 38,
      fontSize: "0.875rem",
    },
  },

  // Header row: search + button
  headerRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    width: "100%",
    flexWrap: "wrap",
  },
}));

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------
const reducer = (state, action) => {
  if (action.type === "LOAD_PROMPTS") {
    const prompts = action.payload;
    const newPrompts = [];
    prompts.forEach((prompt) => {
      const idx = state.findIndex((p) => p.id === prompt.id);
      if (idx !== -1) {
        state[idx] = prompt;
      } else {
        newPrompts.push(prompt);
      }
    });
    return [...state, ...newPrompts];
  }

  if (action.type === "UPDATE_PROMPTS") {
    const prompt = action.payload;
    const idx = state.findIndex((p) => p.id === prompt.id);
    if (idx !== -1) {
      state[idx] = prompt;
      return [...state];
    }
    return [prompt, ...state];
  }

  if (action.type === "DELETE_PROMPT") {
    const promptId = action.payload;
    const idx = state.findIndex((p) => p.id === promptId);
    if (idx !== -1) state.splice(idx, 1);
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  return state;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const Prompts = () => {
  const classes = useStyles();

  const [prompts, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");

  const [promptModalOpen, setPromptModalOpen] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const { user } = useContext(AuthContext);
  const { getPlanCompany } = usePlans();
  const history = useHistory();
  const companyId = user.companyId;
  const socketManager = useContext(SocketContext);

  // ── Plan check ─────────────────────────────────────────────────────────
  useEffect(() => {
    async function fetchData() {
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs.plan.useOpenAi) {
        toast.error(
          "Esta empresa não possui permissão para acessar essa página! Estamos lhe redirecionando."
        );
        setTimeout(() => history.push("/"), 1000);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Initial data fetch ─────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/prompt");
        dispatch({ type: "LOAD_PROMPTS", payload: data.prompts });
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ── WebSocket ──────────────────────────────────────────────────────────
  useEffect(() => {
    const socket = socketManager.getSocket(companyId);

    socket.on(`company-${companyId}-prompt`, (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_PROMPTS", payload: data.prompt });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_PROMPT", payload: data.promptId });
      }
    });

    return () => socket.disconnect();
  }, [companyId, socketManager]);

  // ── Handlers ───────────────────────────────────────────────────────────
  const handleOpenPromptModal = () => {
    setSelectedPrompt(null);
    setPromptModalOpen(true);
  };

  const handleClosePromptModal = () => {
    setSelectedPrompt(null);
    setPromptModalOpen(false);
  };

  const handleEditPrompt = (prompt) => {
    setSelectedPrompt(prompt);
    setPromptModalOpen(true);
  };

  const handleCloseConfirmationModal = () => {
    setConfirmModalOpen(false);
    setSelectedPrompt(null);
  };

  const handleDeletePrompt = async (promptId) => {
    try {
      const { data } = await api.delete(`/prompt/${promptId}`);
      toast.info(i18n.t(data.message));
    } catch (err) {
      toastError(err);
    }
    setSelectedPrompt(null);
  };

  const handleToggleActive = async (prompt) => {
    try {
      await api.patch(`/prompt/${prompt.id}/toggle-active`);
      toast.success(prompt.isActive ? "Agente desativado" : "Agente ativado");
    } catch (err) {
      toastError(err);
    }
  };

  // ── Filtered list ──────────────────────────────────────────────────────
  const filteredPrompts = prompts.filter((p) =>
    p.name?.toLowerCase().includes(searchParam.toLowerCase())
  );

  const showEmpty = !loading && filteredPrompts.length === 0;

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <MainContainer>
      {/* Info banner */}
      <Paper className={classes.agentInfoBox} variant="outlined">
        <Typography variant="body1" className={classes.agentInfoText}>
          <strong>Agente IA</strong> centraliza provedores, chaves, modelo e
          contexto RAG em um só lugar. Configure a personalidade e
          especialização de cada agente para um atendimento humanizado.
        </Typography>
        <Typography variant="body1" className={classes.agentLinks}>
          <strong>Docs:</strong>
          <br />
          Agente IA:{" "}
          <a
            href="https://platform.openai.com/api-keys"
            target="_blank"
            rel="noopener noreferrer"
          >
            API Keys
          </a>{" "}
          ·{" "}
          <a
            href="https://platform.openai.com/usage"
            target="_blank"
            rel="noopener noreferrer"
          >
            Usage
          </a>
          <br />
          Groq:{" "}
          <a
            href="https://console.groq.com/keys"
            target="_blank"
            rel="noopener noreferrer"
          >
            Keys
          </a>{" "}
          ·{" "}
          <a
            href="https://console.groq.com/docs"
            target="_blank"
            rel="noopener noreferrer"
          >
            Docs
          </a>
        </Typography>
      </Paper>

      {/* Modals */}
      <ConfirmationModal
        title={
          selectedPrompt &&
          `${i18n.t("prompts.confirmationModal.deleteTitle")} ${selectedPrompt.name}?`
        }
        open={confirmModalOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={() => handleDeletePrompt(selectedPrompt.id)}
      >
        {i18n.t("prompts.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <PromptModal
        open={promptModalOpen}
        onClose={handleClosePromptModal}
        promptId={selectedPrompt?.id}
      />

      {/* Header: Title + Search + Add button */}
      <MainHeader>
        <Title>{i18n.t("prompts.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Box className={classes.headerRow}>
            <TextField
              className={classes.searchField}
              variant="outlined"
              size="small"
              placeholder="Buscar agente..."
              value={searchParam}
              onChange={(e) => setSearchParam(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" style={{ opacity: 0.5 }} />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenPromptModal}
            >
              {i18n.t("prompts.buttons.add")}
            </Button>
          </Box>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      {/* Table */}
      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="left">Status</TableCell>
              <TableCell align="left">{i18n.t("prompts.table.name")}</TableCell>
              <TableCell align="left">Agente</TableCell>
              <TableCell align="left">{i18n.t("prompts.table.queue")}</TableCell>
              <TableCell align="left">{i18n.t("prompts.table.max_tokens")}</TableCell>
              <TableCell align="center">{i18n.t("prompts.table.actions")}</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {/* Loading skeleton */}
            {loading && <TableRowSkeleton columns={6} />}

            {/* Rows */}
            {!loading &&
              filteredPrompts.map((prompt) => (
                <TableRow key={prompt.id}>
                  {/* Status */}
                  <TableCell align="left">
                    <Chip
                      size="small"
                      variant="outlined"
                      label={prompt.isActive !== false ? "Ativo" : "Inativo"}
                      className={
                        prompt.isActive !== false
                          ? classes.chipActive
                          : classes.chipInactive
                      }
                    />
                  </TableCell>

                  {/* Name */}
                  <TableCell align="left">{prompt.name}</TableCell>

                  {/* Agent name + icon */}
                  <TableCell align="left">
                    {prompt.agentName ? (
                      <Box className={classes.agentCell}>
                        <Android className={classes.agentIcon} />
                        <span>{prompt.agentName}</span>
                      </Box>
                    ) : (
                      <span style={{ opacity: 0.35 }}>—</span>
                    )}
                  </TableCell>

                  {/* Queues */}
                  <TableCell align="left">
                    {prompt.queues && prompt.queues.length > 0 ? (
                      prompt.queues.map((q) => (
                        <Chip
                          key={q.id}
                          label={q.name}
                          size="small"
                          variant="outlined"
                          className={classes.queueChip}
                          style={{ borderColor: q.color, color: q.color }}
                        />
                      ))
                    ) : prompt.queue?.name ? (
                      <Chip
                        label={prompt.queue.name}
                        size="small"
                        variant="outlined"
                        className={classes.queueChip}
                      />
                    ) : (
                      <Chip
                        label="Todas as Filas"
                        size="small"
                        variant="outlined"
                        className={classes.allQueuesChip}
                      />
                    )}
                  </TableCell>

                  {/* Max tokens */}
                  <TableCell align="left">{prompt.maxTokens}</TableCell>

                  {/* Actions */}
                  <TableCell align="center">
                    <Tooltip
                      title={
                        prompt.isActive !== false
                          ? "Desativar agente"
                          : "Ativar agente"
                      }
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleToggleActive(prompt)}
                        style={{
                          color:
                            prompt.isActive !== false ? "#43a047" : undefined,
                        }}
                      >
                        <PowerSettingsNew />
                      </IconButton>
                    </Tooltip>

                    <IconButton
                      size="small"
                      onClick={() => handleEditPrompt(prompt)}
                    >
                      <Edit />
                    </IconButton>

                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedPrompt(prompt);
                        setConfirmModalOpen(true);
                      }}
                    >
                      <DeleteOutline />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}

            {/* Empty state */}
            {showEmpty && (
              <TableRow className={classes.emptyStateRow}>
                <TableCell colSpan={6}>
                  <Box className={classes.emptyStateBox}>
                    <Android className={classes.emptyStateIcon} />
                    <Typography className={classes.emptyStateText}>
                      {searchParam
                        ? `Nenhum Agente IA encontrado para "${searchParam}".`
                        : "Nenhum Agente IA encontrado."}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Prompts;